import { payFromAccount } from '../budget/accounts';
import { resolveDeductFund } from '../budget/settings';
import { GROCERIES_KEY } from '../shopping/items';
import type { DomainEvent } from '../events';
import { insertMutation, updateMutation, type Mutation } from '../mutations';
import { defineRule } from './engine';

/** A time strictly after `after`, so a follow-up write wins last-write-wins over the one before it. */
function laterThan(now: string, after: string): string {
  const t = Date.parse(after);
  return Date.parse(now) > t ? now : new Date(t + 1).toISOString();
}

/**
 * Spec §8 shopping.checked (v0.2 name: shopping.bought) -> write one expense when Spending is
 * usable: the trip total, category Groceries (if the user has it), the items as the note, source
 * 'shopping'. Every field is in the expense insert, nothing waits on a later write: the id is the
 * one buildBuy already put on the trip, and the fund (resolveDeductFund) is written in the insert.
 * The follow-up expense.created lets budget.deduct join the chain (Shopping ┄ Spending ┄ Budget).
 * Without Spending the rule doesn't run, so the trip is recorded with no expense.
 */
export const shoppingExpenseRule = defineRule<'shopping.bought'>({
  id: 'shopping.expense',
  on: 'shopping.bought',
  touches: ['shopping', 'spending'],
  run(event, ctx) {
    const at = laterThan(ctx.now, event.at);
    const categoryId = ctx.read.getCategoryByKey(GROCERIES_KEY)?.id ?? null;
    const settings = ctx.read.getBudgetSettings();
    const fundId = resolveDeductFund(null, settings, ctx.usable('budget'));
    // v0.7.6: a card picked on the buy sheet charges the card, never an account.
    const method = event.method?.startsWith('card:') ? event.method : null;
    // v0.5: the account picked on the buy sheet, else the fund's default account or "Pay from".
    const accountId = method
      ? null
      : event.accountId !== undefined
        ? event.accountId
        : payFromAccount(
            null,
            fundId ? (ctx.read.getFund?.(fundId) ?? null) : null,
            settings,
            ctx.usable('budget'),
            ctx.read.getAccounts?.(),
          );
    const insert = insertMutation({
      table: 'ledger_entries',
      source: 'shopping',
      at,
      ...(event.expenseId ? { rowId: event.expenseId } : {}),
      values: {
        date: event.date,
        amountMinor: event.totalMinor,
        currency: 'PHP',
        kind: 'expense',
        categoryId,
        method,
        fundId,
        accountId,
        sourceModule: 'shopping',
        sourceId: event.tripId,
        note: event.note,
        recordedTz: ctx.tz,
      },
    });
    const mutations: Mutation[] = [insert];
    // Only for a producer that didn't pick the id up front: link the trip afterwards.
    if (!event.expenseId) {
      mutations.push(
        updateMutation({
          table: 'shopping_trips',
          rowId: event.tripId,
          values: { expenseId: insert.rowId },
          source: 'shopping',
          at,
        }),
      );
    }
    const created: DomainEvent = {
      type: 'expense.created',
      expenseId: insert.rowId,
      amountMinor: event.totalMinor,
      categoryId,
      fundId,
      date: event.date,
      method,
      sourceModule: 'shopping',
      at,
      mutationId: insert.id,
    };
    return { mutations, events: [created] };
  },
});
